import React, { useState, useEffect } from 'react';
import microbiologyService from '../../../services/microbiologyService';
import CommonPagination from '../../../components/common/CommonPagination';
import usePagination from '../../../hooks/usePagination';
import './MicrobiologyMasters.css';

export default function AntimicrobialMasterPage() {
  const [antimicrobials, setAntimicrobials] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDrug, setEditingDrug] = useState(null);

  // Centralized Standardized Pagination
  const pagination = usePagination({
    items: antimicrobials,
    initialPage: 1,
    initialPageSize: 15,
    resetDeps: [search, selectedClass]
  });

  // Extended form state for Antimicrobial Master
  const [formData, setFormData] = useState({
    drugCode: '',
    drugName: '',
    shortName: '',
    displayName: '',
    tradeReference: '',
    classID: '',
    subclass: '',
    mechanismClass: 'Cell Wall Synthesis Inhibitor',
    antimicrobialType: 'Antibiotic',
    drugGroup: 'Group A',
    astApplicable: true,
    defaultReportable: true,
    routineTesting: true,
    surveillanceRelevant: false,
    stewardshipRelevant: true,
    routeOfAdministration: 'IV/Oral',
    stewardshipCategory: 'Access',
    restrictedUse: false,
    clinicalNotes: '',
    stewardshipNotes: '',
    synonyms: '',
    status: 'Draft',
    displayOrder: 0,
    remarks: '',
    referenceSource: 'CLSI M100 / WHO AWaRe',
    referenceVersion: '2026.1 (M100-ED34)',
    lastReviewedDate: new Date().toISOString().split('T')[0],
    reviewStatus: 'Approved',
    version: '1.0',
    effectiveFrom: new Date().toISOString().split('T')[0]
  });

  const [tradeTags, setTradeTags] = useState([]);
  const [tradeInput, setTradeInput] = useState('');
  const [synonymTags, setSynonymTags] = useState([]);
  const [synonymInput, setSynonymInput] = useState('');
  const [validationErrors, setValidationErrors] = useState({});

  const loadData = async () => {
    try {
      setLoading(true);
      const [drugRes, classRes] = await Promise.all([
        microbiologyService.getAntimicrobials({
          search: search || undefined,
          classId: selectedClass || undefined
        }),
        microbiologyService.getAntimicrobialClasses()
      ]);
      setAntimicrobials(drugRes?.data || []);
      setClasses(classRes?.data || []);
    } catch (err) {
      console.error('Error loading antimicrobials:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [search, selectedClass]);

  const handleOpenAdd = () => {
    setEditingDrug(null);
    setValidationErrors({});
    setTradeTags([]);
    setTradeInput('');
    setSynonymTags([]);
    setSynonymInput('');
    setFormData({
      drugCode: '',
      drugName: '',
      shortName: '',
      displayName: '',
      tradeReference: '',
      classID: classes[0]?.classID || 1,
      subclass: '',
      mechanismClass: 'Cell Wall Synthesis Inhibitor',
      antimicrobialType: 'Antibiotic',
      drugGroup: 'Group A',
      astApplicable: true,
      defaultReportable: true,
      routineTesting: true,
      surveillanceRelevant: false,
      stewardshipRelevant: true,
      routeOfAdministration: 'IV/Oral',
      stewardshipCategory: 'Access',
      restrictedUse: false,
      clinicalNotes: '',
      stewardshipNotes: '',
      synonyms: '',
      status: 'Draft',
      displayOrder: antimicrobials.length + 1,
      remarks: '',
      referenceSource: 'CLSI M100 / WHO AWaRe',
      referenceVersion: '2026.1 (M100-ED34)',
      lastReviewedDate: new Date().toISOString().split('T')[0],
      reviewStatus: 'Draft',
      version: '1.0',
      effectiveFrom: new Date().toISOString().split('T')[0]
    });
    setIsModalOpen(true);
  };

  const handleOpenEdit = (drug) => {
    setEditingDrug(drug);
    setValidationErrors({});
    
    // Parse synonyms & trade refs if present
    const existingSynonyms = drug.shortName && drug.shortName !== drug.drugName
      ? [drug.shortName]
      : [];
    setSynonymTags(existingSynonyms);
    setSynonymInput('');
    setTradeTags([]);
    setTradeInput('');

    setFormData({
      antimicrobialID: drug.antimicrobialID,
      drugCode: drug.drugCode || '',
      drugName: drug.drugName || '',
      shortName: drug.shortName || '',
      displayName: drug.shortName ? `${drug.drugName} (${drug.shortName})` : drug.drugName,
      tradeReference: '',
      classID: drug.classID || classes[0]?.classID || 1,
      subclass: drug.subclass || '',
      mechanismClass: 'Cell Wall Synthesis Inhibitor',
      antimicrobialType: 'Antibiotic',
      drugGroup: drug.drugGroup || 'Group A',
      astApplicable: true,
      defaultReportable: drug.defaultReportable !== false,
      routineTesting: drug.drugGroup === 'Group A',
      surveillanceRelevant: drug.drugGroup === 'Group B' || drug.drugGroup === 'Group C',
      stewardshipRelevant: true,
      routeOfAdministration: drug.routeOfAdministration || 'IV/Oral',
      stewardshipCategory: drug.drugGroup === 'Group C' ? 'Reserve' : drug.drugGroup === 'Group B' ? 'Watch' : 'Access',
      restrictedUse: drug.drugGroup === 'Group C',
      clinicalNotes: '',
      stewardshipNotes: '',
      synonyms: '',
      status: drug.isActive ? 'Active' : 'Inactive',
      displayOrder: drug.displayOrder || 0,
      remarks: drug.remarks || '',
      referenceSource: 'CLSI M100 / WHO AWaRe',
      referenceVersion: '2026.1 (M100-ED34)',
      lastReviewedDate: new Date().toISOString().split('T')[0],
      reviewStatus: 'Approved',
      version: '1.0',
      effectiveFrom: new Date().toISOString().split('T')[0]
    });
    setIsModalOpen(true);
  };

  // Synchronize generic name with short name and display name
  const handleGenericNameChange = (val) => {
    setFormData((prev) => ({
      ...prev,
      drugName: val,
      displayName: prev.shortName ? `${val} (${prev.shortName})` : val
    }));

    if (validationErrors.drugName) {
      setValidationErrors((prev) => ({ ...prev, drugName: null }));
    }
  };

  const handleAddTradeTag = (e) => {
    if (e) e.preventDefault();
    const clean = tradeInput.trim();
    if (!clean) return;
    if (tradeTags.some((t) => t.toLowerCase() === clean.toLowerCase())) {
      setValidationErrors((prev) => ({ ...prev, trade: 'Duplicate brand reference already added' }));
      return;
    }
    const updated = [...tradeTags, clean];
    setTradeTags(updated);
    setTradeInput('');
    setFormData((prev) => ({ ...prev, tradeReference: updated.join(', ') }));
    setValidationErrors((prev) => ({ ...prev, trade: null }));
  };

  const handleRemoveTradeTag = (tagToRemove) => {
    const updated = tradeTags.filter((t) => t !== tagToRemove);
    setTradeTags(updated);
    setFormData((prev) => ({ ...prev, tradeReference: updated.join(', ') }));
  };

  const handleAddSynonymTag = (e) => {
    if (e) e.preventDefault();
    const clean = synonymInput.trim();
    if (!clean) return;
    if (synonymTags.some((t) => t.toLowerCase() === clean.toLowerCase())) {
      setValidationErrors((prev) => ({ ...prev, synonyms: 'Duplicate synonym already added' }));
      return;
    }
    const updated = [...synonymTags, clean];
    setSynonymTags(updated);
    setSynonymInput('');
    setValidationErrors((prev) => ({ ...prev, synonyms: null }));
  };

  const handleRemoveSynonymTag = (tagToRemove) => {
    const updated = synonymTags.filter((t) => t !== tagToRemove);
    setSynonymTags(updated);
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.drugCode.trim()) {
      errors.drugCode = 'Drug Code is required (e.g. CRO, MEM, AMK)';
    } else {
      const isDuplicate = antimicrobials.some(
        (d) =>
          d.drugCode?.toUpperCase() === formData.drugCode.trim().toUpperCase() &&
          d.antimicrobialID !== formData.antimicrobialID
      );
      if (isDuplicate) {
        errors.drugCode = 'Drug Code already exists in antimicrobial master';
      }
    }

    if (!formData.drugName.trim()) {
      errors.drugName = 'Generic / INN Drug Name is required (e.g. Ceftriaxone)';
    }

    if (!formData.classID) {
      errors.classID = 'Pharmacological Class is required';
    }

    if (!formData.antimicrobialType) {
      errors.antimicrobialType = 'Antimicrobial Type is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async (targetStatus) => {
    if (!validateForm()) {
      return;
    }

    try {
      // Build structured remarks to preserve clinical stewardship & reference metadata
      let combinedRemarks = formData.remarks.trim();
      const metaParts = [];
      if (formData.stewardshipCategory) metaParts.push(`AWaRe: ${formData.stewardshipCategory}`);
      if (formData.restrictedUse) metaParts.push('Restricted Use');
      if (tradeTags.length > 0) metaParts.push(`Brands: ${tradeTags.join(', ')}`);
      if (formData.clinicalNotes.trim()) metaParts.push(`Clinical: ${formData.clinicalNotes.trim()}`);
      if (formData.stewardshipNotes.trim()) metaParts.push(`Stewardship: ${formData.stewardshipNotes.trim()}`);

      if (metaParts.length > 0) {
        combinedRemarks = combinedRemarks
          ? `${combinedRemarks} | [${metaParts.join(' | ')}]`
          : `[${metaParts.join(' | ')}]`;
      }

      const payload = {
        antimicrobialID: formData.antimicrobialID,
        drugCode: formData.drugCode.trim().toUpperCase(),
        drugName: formData.drugName.trim(),
        shortName: formData.shortName.trim() || formData.drugCode.trim().toUpperCase(),
        classID: parseInt(formData.classID, 10),
        subclass: formData.subclass.trim() || null,
        drugGroup: formData.drugGroup,
        defaultReportable: formData.defaultReportable,
        routeOfAdministration: formData.routeOfAdministration || null,
        displayOrder: parseInt(formData.displayOrder, 10) || 0,
        remarks: combinedRemarks || null,
        isActive: targetStatus === 'Active'
      };

      await microbiologyService.saveAntimicrobial(payload);
      setIsModalOpen(false);
      loadData();
    } catch (err) {
      setValidationErrors((prev) => ({
        ...prev,
        submit: err.response?.data?.message || 'Failed to save antimicrobial agent. Please check server.'
      }));
    }
  };

  return (
    <div className="micro-master-container">
      <div className="micro-header-section">
        <div className="micro-header-title">
          <h1>
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.2">
              <path d="m10.5 20.5 10-10a4.95 4.95 0 1 0-7-7l-10 10a4.95 4.95 0 1 0 7 7Z" />
              <path d="m8.5 8.5 7 7" />
            </svg>
            Antimicrobial &amp; Antibiotic Master
          </h1>
          <p>Pharmacological antibiotic library, class classification, and CLSI Group A/B/C/U stewardship tiers</p>
        </div>
        <div className="micro-header-actions">
          <button className="micro-btn-primary" onClick={handleOpenAdd}>
            + Add New Antibiotic
          </button>
        </div>
      </div>

      <div className="micro-filter-bar">
        <div className="micro-search-input">
          <input
            type="text"
            placeholder="Search by antibiotic name, code or short abbreviation..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <select
          className="micro-filter-select"
          value={selectedClass}
          onChange={(e) => setSelectedClass(e.target.value)}
        >
          <option value="">All Pharmacological Classes</option>
          {classes.map((c) => (
            <option key={c.classID} value={c.classID}>
              {c.className}
            </option>
          ))}
        </select>
      </div>

      <div className="micro-table-wrapper">
        <table className="micro-table">
          <thead>
            <tr>
              <th>Drug Code</th>
              <th>Generic Drug Name</th>
              <th>Abbreviation</th>
              <th>Class</th>
              <th>Subclass</th>
              <th>Testing Group</th>
              <th>Default Reportable</th>
              <th>Route</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="10" style={{ textAlign: 'center', padding: '32px' }}>
                  Loading antimicrobial agents...
                </td>
              </tr>
            ) : antimicrobials.length === 0 ? (
              <tr>
                <td colSpan="10" style={{ textAlign: 'center', padding: '32px' }}>
                  No antimicrobial drugs matched your filter criteria.
                </td>
              </tr>
            ) : (
              pagination.pagedItems.map((drug) => (
                <tr key={drug.antimicrobialID}>
                  <td>
                    <strong>{drug.drugCode}</strong>
                  </td>
                  <td>{drug.drugName}</td>
                  <td>{drug.shortName || '—'}</td>
                  <td>{drug.className}</td>
                  <td>{drug.subclass || '—'}</td>
                  <td>
                    <span
                      className="micro-badge"
                      style={{
                        background: drug.drugGroup === 'Group A' ? '#e0f2fe' : drug.drugGroup === 'Group B' ? '#fef3c7' : drug.drugGroup === 'Group C' ? '#fee2e2' : '#f1f5f9',
                        color: drug.drugGroup === 'Group A' ? '#0369a1' : drug.drugGroup === 'Group B' ? '#b45309' : drug.drugGroup === 'Group C' ? '#b91c1c' : '#475569'
                      }}
                    >
                      {drug.drugGroup || 'Group A'}
                    </span>
                  </td>
                  <td>
                    <span
                      className="micro-badge"
                      style={{
                        background: drug.defaultReportable ? '#dcfce7' : '#fee2e2',
                        color: drug.defaultReportable ? '#15803d' : '#b91c1c'
                      }}
                    >
                      {drug.defaultReportable ? 'Yes (Reportable)' : 'Suppressed'}
                    </span>
                  </td>
                  <td>{drug.routeOfAdministration || 'IV/Oral'}</td>
                  <td>
                    <span
                      className="micro-badge"
                      style={{
                        background: drug.isActive ? '#dcfce7' : '#f1f5f9',
                        color: drug.isActive ? '#15803d' : '#64748b'
                      }}
                    >
                      {drug.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <button
                      className="micro-btn-secondary"
                      style={{ padding: '4px 10px', fontSize: '0.78rem' }}
                      onClick={() => handleOpenEdit(drug)}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Centralized Standard LIMS Pagination */}
      <CommonPagination
        {...pagination.paginationProps}
        loading={loading}
      />

      {/* Redesigned Add / Edit Antimicrobial Agent Form Modal */}
      {isModalOpen && (
        <div className="micro-modal-overlay" role="dialog" aria-modal="true">
          <div className="micro-modal-content">
            {/* Header */}
            <div className="micro-modal-header">
              <div className="micro-modal-header-titles">
                <h3>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.2">
                    <path d="m10.5 20.5 10-10a4.95 4.95 0 1 0-7-7l-10 10a4.95 4.95 0 1 0 7 7Z" />
                    <path d="m8.5 8.5 7 7" />
                  </svg>
                  {editingDrug ? 'Edit Antimicrobial Agent' : 'Add Antimicrobial Agent'}
                </h3>
                <p>Define and maintain standardized antimicrobial laboratory master data</p>
              </div>
              <button
                className="micro-modal-close-btn"
                onClick={() => setIsModalOpen(false)}
                title="Close"
                aria-label="Close form"
              >
                &times;
              </button>
            </div>

            {/* Scrollable Form Body */}
            <div className="micro-modal-body">
              {/* Top Record Summary Badge */}
              <div className="micro-summary-bar">
                <div className="micro-summary-item">
                  <strong>Code:</strong> {formData.drugCode || '—'}
                </div>
                <span className="micro-summary-divider">|</span>
                <div className="micro-summary-item">
                  <strong>Generic:</strong> {formData.drugName || '—'}
                </div>
                <span className="micro-summary-divider">|</span>
                <div className="micro-summary-item">
                  <strong>Type:</strong> {formData.antimicrobialType}
                </div>
                <span className="micro-summary-divider">|</span>
                <div className="micro-summary-item">
                  <strong>Status:</strong> <span style={{ textTransform: 'uppercase', fontWeight: 700 }}>{formData.status}</span>
                </div>
                <span className="micro-summary-divider">|</span>
                <div className="micro-summary-item">
                  <strong>AWaRe:</strong> {formData.stewardshipCategory}
                </div>
              </div>

              {validationErrors.submit && (
                <div style={{ padding: '10px 14px', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: '6px', color: '#991b1b', fontSize: '0.84rem' }}>
                  {validationErrors.submit}
                </div>
              )}

              {/* SECTION 1: Antimicrobial Identity */}
              <div className="micro-section-card">
                <div className="micro-section-title">
                  <span>1. Antimicrobial Identity</span>
                  <span className="section-badge">Required Identity</span>
                </div>
                <div className="micro-form-grid-2">
                  <div className="micro-form-group">
                    <label>
                      <span>Drug Code <span className="req-star">*</span></span>
                    </label>
                    <input
                      type="text"
                      className={validationErrors.drugCode ? 'is-invalid' : ''}
                      placeholder="e.g. CRO, MEM, AMK, VAN"
                      value={formData.drugCode}
                      onChange={(e) => {
                        setFormData({ ...formData, drugCode: e.target.value.toUpperCase() });
                        if (validationErrors.drugCode) setValidationErrors({ ...validationErrors, drugCode: null });
                      }}
                    />
                    {validationErrors.drugCode ? (
                      <span className="micro-form-error">{validationErrors.drugCode}</span>
                    ) : (
                      <span className="micro-form-helper">Unique 2–5 character standardized laboratory identifier code</span>
                    )}
                  </div>

                  <div className="micro-form-group">
                    <label>
                      <span>Generic / INN Name <span className="req-star">*</span></span>
                    </label>
                    <input
                      type="text"
                      className={validationErrors.drugName ? 'is-invalid' : ''}
                      placeholder="e.g. Ceftriaxone, Meropenem"
                      value={formData.drugName}
                      onChange={(e) => handleGenericNameChange(e.target.value)}
                    />
                    {validationErrors.drugName ? (
                      <span className="micro-form-error">{validationErrors.drugName}</span>
                    ) : (
                      <span className="micro-form-helper">International Nonproprietary Name (INN)</span>
                    )}
                  </div>

                  <div className="micro-form-group">
                    <label>Short Name / Abbreviation</label>
                    <input
                      type="text"
                      placeholder="e.g. Ceftriaxone or CRO"
                      value={formData.shortName}
                      onChange={(e) => {
                        const sVal = e.target.value;
                        setFormData({
                          ...formData,
                          shortName: sVal,
                          displayName: sVal ? `${formData.drugName} (${sVal})` : formData.drugName
                        });
                      }}
                    />
                    <span className="micro-form-helper">Standardized clinical abbreviation for compact displays</span>
                  </div>

                  <div className="micro-form-group">
                    <label>Display Name</label>
                    <input
                      type="text"
                      placeholder="e.g. Ceftriaxone (CRO)"
                      value={formData.displayName}
                      onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                    />
                    <span className="micro-form-helper">Laboratory &amp; patient report-facing representation</span>
                  </div>

                  {/* Trade / Brand Reference */}
                  <div className="micro-form-group" style={{ gridColumn: '1 / -1' }}>
                    <label>
                      <span>Trade / Brand Reference</span>
                      <span style={{ fontSize: '0.74rem', color: '#64748b' }}>Press Enter to add brand tag (optional)</span>
                    </label>
                    <div className="micro-tag-input-box">
                      {tradeTags.map((t) => (
                        <span key={t} className="micro-tag-chip">
                          {t}
                          <button
                            type="button"
                            onClick={() => handleRemoveTradeTag(t)}
                            title="Remove brand"
                          >
                            &times;
                          </button>
                        </span>
                      ))}
                      <input
                        type="text"
                        className="micro-tag-input-field"
                        placeholder={tradeTags.length === 0 ? "Type brand name (e.g. Rocephin) & press Enter..." : "Add another brand..."}
                        value={tradeInput}
                        onChange={(e) => setTradeInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ',') {
                            e.preventDefault();
                            handleAddTradeTag();
                          }
                        }}
                      />
                    </div>
                    {validationErrors.trade && (
                      <span className="micro-form-error">{validationErrors.trade}</span>
                    )}
                  </div>
                </div>
              </div>

              {/* SECTION 2: Pharmacological Classification */}
              <div className="micro-section-card">
                <div className="micro-section-title">
                  <span>2. Pharmacological Classification</span>
                  <span className="section-badge">Drug Family &amp; Class</span>
                </div>
                <div className="micro-form-grid-4">
                  <div className="micro-form-group">
                    <label>
                      <span>Pharmacological Class <span className="req-star">*</span></span>
                    </label>
                    <select
                      className={validationErrors.classID ? 'is-invalid' : ''}
                      value={formData.classID}
                      onChange={(e) => {
                        setFormData({ ...formData, classID: e.target.value });
                        if (validationErrors.classID) setValidationErrors({ ...validationErrors, classID: null });
                      }}
                    >
                      <option value="">Select Class...</option>
                      {classes.map((c) => (
                        <option key={c.classID} value={c.classID}>
                          {c.className}
                        </option>
                      ))}
                    </select>
                    {validationErrors.classID && (
                      <span className="micro-form-error">{validationErrors.classID}</span>
                    )}
                  </div>

                  <div className="micro-form-group">
                    <label>Subclass / Generation</label>
                    <input
                      type="text"
                      placeholder="e.g. 3rd Gen Cephalosporin"
                      value={formData.subclass}
                      onChange={(e) => setFormData({ ...formData, subclass: e.target.value })}
                    />
                  </div>

                  <div className="micro-form-group">
                    <label>Mechanism / Target Class</label>
                    <select
                      value={formData.mechanismClass}
                      onChange={(e) => setFormData({ ...formData, mechanismClass: e.target.value })}
                    >
                      <option value="Cell Wall Synthesis Inhibitor">Cell Wall Synthesis Inhibitor</option>
                      <option value="Protein Synthesis Inhibitor (30S/50S)">Protein Synthesis Inhibitor (30S/50S)</option>
                      <option value="DNA Gyrase / Topoisomerase Inhibitor">DNA Gyrase / Topoisomerase Inhibitor</option>
                      <option value="Folate Synthesis Inhibitor">Folate Synthesis Inhibitor</option>
                      <option value="Cell Membrane Disruptor">Cell Membrane Disruptor</option>
                      <option value="RNA Polymerase Inhibitor">RNA Polymerase Inhibitor</option>
                      <option value="Not Applicable">Not Applicable</option>
                    </select>
                  </div>

                  <div className="micro-form-group">
                    <label>
                      <span>Antimicrobial Type <span className="req-star">*</span></span>
                    </label>
                    <select
                      value={formData.antimicrobialType}
                      onChange={(e) => setFormData({ ...formData, antimicrobialType: e.target.value })}
                    >
                      <option value="Antibiotic">Antibiotic (Antibacterial)</option>
                      <option value="Antifungal">Antifungal</option>
                      <option value="Antiviral">Antiviral</option>
                      <option value="Antiparasitic">Antiparasitic</option>
                      <option value="Antimycobacterial">Antimycobacterial</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* SECTION 3: Laboratory / AST Relevance */}
              <div className="micro-section-card">
                <div className="micro-section-title">
                  <span>3. Laboratory &amp; AST Relevance</span>
                  <span className="section-badge">Testing &amp; Reporting Scope</span>
                </div>
                <div className="micro-form-grid-3">
                  <div className="micro-form-group">
                    <label>Laboratory Testing Group (CLSI Tier)</label>
                    <select
                      value={formData.drugGroup}
                      onChange={(e) => setFormData({ ...formData, drugGroup: e.target.value })}
                    >
                      <option value="Group A">Group A (Primary Routine Testing)</option>
                      <option value="Group B">Group B (Secondary / Severe Infection)</option>
                      <option value="Group C">Group C (Supplemental / Alternative)</option>
                      <option value="Group U">Group U (Urinary Tract Specific Only)</option>
                      <option value="Group O">Group O (Other / Institutional Policy)</option>
                    </select>
                    <span className="micro-form-helper">Classification used to organize antimicrobial testing and selective reporting</span>
                  </div>

                  <div className="micro-form-group">
                    <label>Route of Administration</label>
                    <select
                      value={formData.routeOfAdministration}
                      onChange={(e) => setFormData({ ...formData, routeOfAdministration: e.target.value })}
                    >
                      <option value="IV/Oral">IV / Oral (Systemic)</option>
                      <option value="IV Only">IV Only (Injectable)</option>
                      <option value="Oral Only">Oral Only</option>
                      <option value="Topical">Topical</option>
                      <option value="Inhaled">Inhaled</option>
                      <option value="Not Applicable">Not Applicable</option>
                    </select>
                  </div>

                  <div className="micro-form-group" style={{ justifyContent: 'center' }}>
                    <label className="micro-switch-label">
                      <div
                        className={`micro-switch-toggle ${formData.defaultReportable ? 'active-green' : ''}`}
                        onClick={() => setFormData({ ...formData, defaultReportable: !formData.defaultReportable })}
                      />
                      <span>Default Reportable on Final Report</span>
                    </label>
                    <span className="micro-form-helper">Controls whether this drug is reported routinely according to configured rules</span>
                  </div>
                </div>

                {/* AST & Surveillance Switches */}
                <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', marginTop: '14px', paddingTop: '12px', borderTop: '1px solid #f1f5f9' }}>
                  <label className="micro-switch-label">
                    <div
                      className={`micro-switch-toggle ${formData.astApplicable ? 'active' : ''}`}
                      onClick={() => setFormData({ ...formData, astApplicable: !formData.astApplicable })}
                    />
                    <span>AST Applicable</span>
                  </label>

                  <label className="micro-switch-label">
                    <div
                      className={`micro-switch-toggle ${formData.routineTesting ? 'active' : ''}`}
                      onClick={() => setFormData({ ...formData, routineTesting: !formData.routineTesting })}
                    />
                    <span>Routine First-Line Testing</span>
                  </label>

                  <label className="micro-switch-label">
                    <div
                      className={`micro-switch-toggle ${formData.surveillanceRelevant ? 'active' : ''}`}
                      onClick={() => setFormData({ ...formData, surveillanceRelevant: !formData.surveillanceRelevant })}
                    />
                    <span>AMR Surveillance Relevant</span>
                  </label>
                </div>
              </div>

              {/* SECTION 4: Stewardship & Clinical Information */}
              <div className="micro-section-card">
                <div className="micro-section-title">
                  <span>4. Stewardship &amp; Clinical Information</span>
                  <span className="section-badge">WHO AWaRe &amp; Institutional Policy</span>
                </div>
                <div className="micro-form-grid-3">
                  <div className="micro-form-group">
                    <label>Stewardship Classification (AWaRe)</label>
                    <select
                      value={formData.stewardshipCategory}
                      onChange={(e) => setFormData({ ...formData, stewardshipCategory: e.target.value })}
                    >
                      <option value="Access">Access (First/Second Choice)</option>
                      <option value="Watch">Watch (Higher Resistance Potential)</option>
                      <option value="Reserve">Reserve (Last-Resort Options)</option>
                      <option value="Not Classified">Not Classified</option>
                    </select>
                    <span className="micro-form-helper">Classification is configurable according to institutional &amp; WHO AMR policy</span>
                  </div>

                  <div className="micro-form-group" style={{ justifyContent: 'center' }}>
                    <label className="micro-switch-label">
                      <div
                        className={`micro-switch-toggle ${formData.restrictedUse ? 'active' : ''}`}
                        onClick={() => setFormData({ ...formData, restrictedUse: !formData.restrictedUse })}
                      />
                      <span>Restricted / Pre-Authorization Required</span>
                    </label>
                    <span className="micro-form-helper">Requires infectious disease approval before dispensing</span>
                  </div>

                  <div className="micro-form-group">
                    <label>Display Order Priority</label>
                    <input
                      type="number"
                      min="0"
                      value={formData.displayOrder}
                      onChange={(e) => setFormData({ ...formData, displayOrder: e.target.value })}
                    />
                  </div>
                </div>

                <div className="micro-form-grid-2" style={{ marginTop: '12px' }}>
                  <div className="micro-form-group">
                    <label>Clinical Notes</label>
                    <textarea
                      rows="2"
                      placeholder="e.g. Indicated for severe nosocomial infections and complicated bacteremia..."
                      value={formData.clinicalNotes}
                      onChange={(e) => setFormData({ ...formData, clinicalNotes: e.target.value })}
                    />
                  </div>

                  <div className="micro-form-group">
                    <label>Stewardship Warnings &amp; Institutional Notes</label>
                    <textarea
                      rows="2"
                      placeholder="e.g. Restricted broad-spectrum reserve agent. Monitor renal function..."
                      value={formData.remarks}
                      onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              {/* SECTION 5: Synonyms / Aliases Chip Tags */}
              <div className="micro-section-card">
                <div className="micro-section-title">
                  <span>5. Synonyms &amp; Aliases</span>
                  <span className="section-badge">Chip Tags</span>
                </div>
                <div className="micro-form-group">
                  <label>
                    <span>Alternative Drug Names / Chemical Aliases</span>
                    <span style={{ fontSize: '0.74rem', color: '#64748b' }}>Press Enter to add chip tag</span>
                  </label>
                  <div className="micro-tag-input-box">
                    {synonymTags.map((tag) => (
                      <span key={tag} className="micro-tag-chip">
                        {tag}
                        <button
                          type="button"
                          onClick={() => handleRemoveSynonymTag(tag)}
                          title="Remove synonym"
                        >
                          &times;
                        </button>
                      </span>
                    ))}
                    <input
                      type="text"
                      className="micro-tag-input-field"
                      placeholder={synonymTags.length === 0 ? "Type synonym & press Enter..." : "Add another..."}
                      value={synonymInput}
                      onChange={(e) => setSynonymInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ',') {
                          e.preventDefault();
                          handleAddSynonymTag();
                        }
                      }}
                    />
                  </div>
                  {validationErrors.synonyms && (
                    <span className="micro-form-error">{validationErrors.synonyms}</span>
                  )}
                </div>
              </div>

              {/* SECTION 6: Status & Reference Metadata */}
              <div className="micro-section-card">
                <div className="micro-section-title">
                  <span>6. Lifecycle Status &amp; Reference Metadata</span>
                  <span className="section-badge">Audit &amp; Traceability</span>
                </div>
                <div className="micro-form-grid-4">
                  <div className="micro-form-group">
                    <label>Status <span className="req-star">*</span></label>
                    <div className="micro-status-group">
                      {['Draft', 'Active', 'Inactive', 'Deprecated'].map((st) => (
                        <button
                          key={st}
                          type="button"
                          className={`micro-status-btn ${formData.status === st ? `active-status-${st.toLowerCase()}` : ''}`}
                          onClick={() => setFormData({ ...formData, status: st })}
                        >
                          {st}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="micro-form-group">
                    <label>Reference Source</label>
                    <input
                      type="text"
                      placeholder="e.g. CLSI M100 / WHO"
                      value={formData.referenceSource}
                      onChange={(e) => setFormData({ ...formData, referenceSource: e.target.value })}
                    />
                  </div>

                  <div className="micro-form-group">
                    <label>Reference Version</label>
                    <input
                      type="text"
                      placeholder="e.g. 2026.1 (M100-ED34)"
                      value={formData.referenceVersion}
                      onChange={(e) => setFormData({ ...formData, referenceVersion: e.target.value })}
                    />
                  </div>

                  <div className="micro-form-group">
                    <label>Last Reviewed Date</label>
                    <input
                      type="date"
                      value={formData.lastReviewedDate}
                      onChange={(e) => setFormData({ ...formData, lastReviewedDate: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer with Actions */}
            <div className="micro-modal-footer">
              <div style={{ fontSize: '0.78rem', color: '#64748b' }}>
                <span style={{ color: '#e11d48', fontWeight: 600 }}>*</span> Required standardized laboratory attributes
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="button"
                  className="micro-btn-secondary"
                  onClick={() => setIsModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="micro-btn-secondary"
                  style={{ background: '#f8fafc', borderColor: '#cbd5e1', color: '#334155' }}
                  onClick={() => handleSave('Draft')}
                >
                  Save Draft
                </button>
                <button
                  type="button"
                  className="micro-btn-success"
                  onClick={() => handleSave('Active')}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Save &amp; Activate
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

